/** Lip-sync script pause tokens: `<#0.2#>` (canonical) and legacy `[pause 0.2s]`. */

export const PAUSE_SEC_MIN = 0.01;
export const PAUSE_SEC_MAX = 99.99;

/** @type {RegExp} Must reset `lastIndex` before each scan when reused. */
export const PAUSE_TOKEN_RE = /<#(\d+(?:\.\d{1,2})?)#>|\[pause\s+([\d.]+)s\]/gi;

export const PAUSE_CHIP_CLASS =
  "lip-pause-chip inline-flex max-w-max shrink-0 flex-nowrap items-center gap-[0.28rem] box-border my-0 mx-[0.1rem] align-middle border border-ui-border bg-ui-surface-muted px-[0.45rem] py-0.5 pl-[0.38rem] text-xs font-bold leading-none whitespace-nowrap [word-break:normal] select-none [vertical-align:middle] dark:border-ui-border dark:bg-ui-elevated";

export function escapeHtml(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function textToHtml(s) {
  return escapeHtml(s).replace(/\n/g, "<br>");
}

export function clampPauseSeconds(value) {
  const n = Math.round(Number(value) * 100) / 100;
  return Math.min(PAUSE_SEC_MAX, Math.max(PAUSE_SEC_MIN, n));
}

export function formatPauseSeconds(value) {
  return String(parseFloat(clampPauseSeconds(value).toFixed(2)));
}

export function pauseTokenText(secRaw) {
  return `<#${formatPauseSeconds(secRaw)}#>`;
}

/**
 * Walk contenteditable DOM in document order; preserves all text between pause chips.
 */
export function serializeScriptEditorRoot(root) {
  if (!root) return "";
  const parts = [];

  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) {
      parts.push(node.textContent || "");
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const el = node;
    if (el.classList?.contains("lip-pause-chip")) {
      parts.push(pauseTokenText(el.getAttribute("data-pause-sec") || "0.2"));
      return;
    }
    if (el.tagName === "BR") {
      parts.push("\n");
      return;
    }
    el.childNodes.forEach(walk);
  }

  root.childNodes.forEach(walk);
  return parts.join("").replace(/\u200b/g, "").replace(/\u00a0/g, " ");
}

export function pauseChipClockSvg() {
  return `<svg class="lip-pause-chip__ic inline-block h-[13px] w-[13px] shrink-0 align-middle text-accent-700 dark:text-white/90" width="13" height="13" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><circle cx="12" cy="13" r="7.5" stroke="currentColor" stroke-width="1.75"/><path d="M12 10v3.25l2 1.25M9 3h6M12 3V1.5" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/></svg>`;
}

export function makePauseChipHtml(secRaw) {
  const sec = formatPauseSeconds(secRaw);
  const secHtml = escapeHtml(sec);
  return `<span class="${PAUSE_CHIP_CLASS}" contenteditable="false" data-pause-sec="${secHtml}">${pauseChipClockSvg()}<span class="lip-pause-chip__label shrink-0 whitespace-nowrap text-ui-brand dark:text-ui-brand-legacy">${secHtml}s</span></span>`;
}

/**
 * @param {string} text
 * @returns {Array<{ type: 'text', value: string } | { type: 'pause', seconds: string }>}
 */
export function parseScriptWithPauses(text) {
  const s = String(text ?? "");
  if (!s) return [{ type: "text", value: "" }];

  const re = /<#(\d+(?:\.\d{1,2})?)#>|\[pause\s+([\d.]+)s\]/gi;
  const segments = [];
  let last = 0;
  for (const match of s.matchAll(re)) {
    if (match.index > last) {
      segments.push({ type: "text", value: s.slice(last, match.index) });
    }
    segments.push({ type: "pause", seconds: formatPauseSeconds(match[1] ?? match[2]) });
    last = match.index + match[0].length;
  }
  if (last < s.length) {
    segments.push({ type: "text", value: s.slice(last) });
  }
  if (!segments.length) {
    segments.push({ type: "text", value: s });
  }
  return segments;
}

/** Rich HTML for contenteditable script editor (text escaped; pause chips inline). */
export function scriptWithPausesToHtml(text) {
  const s = text ?? "";
  if (!s.trim()) return "";

  const re = /<#(\d+(?:\.\d{1,2})?)#>|\[pause\s+([\d.]+)s\]/gi;
  let html = "";
  let last = 0;
  for (const match of s.matchAll(re)) {
    const sec = match[1] ?? match[2];
    html += textToHtml(s.slice(last, match.index));
    html += makePauseChipHtml(sec);
    last = match.index + match[0].length;
  }
  html += textToHtml(s.slice(last));
  return html;
}

/** Canonical prompt string from editor serialization (drops empty / whitespace-only). */
export function normalizeScriptPrompt(text) {
  const s = String(text ?? "")
    .replace(/\u200b/g, "")
    .replace(/\u00a0/g, " ");
  return s.trim() ? s : "";
}

/** Whether contenteditable output matches stored prompt (avoids empty `<br>` vs "" fights). */
export function scriptEditorMatchesPrompt(root, prompt) {
  if (!root) return true;
  const fromEditor = normalizeScriptPrompt(serializeScriptEditorRoot(root));
  const fromPrompt = normalizeScriptPrompt(prompt);
  return fromEditor === fromPrompt;
}

/** Plain text for aria labels (pauses as "0.2s"). */
export function scriptWithPausesToPlainText(text) {
  PAUSE_TOKEN_RE.lastIndex = 0;
  return String(text ?? "").replace(PAUSE_TOKEN_RE, (_m, a, b) => `${formatPauseSeconds(a ?? b)}s`);
}
